import {
  OnInit,
  Component,
  Inject,
  ViewChild,
  TemplateRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { PathwayDetailsModel } from '@app/pathways/rsm/pathway-api.model';
import {
  DataColumn,
  DataColumnDate,
  DataColumnPerson,
} from '@app/shared/components/data-table/data-table';
import { AuthService } from '@app/shared/services/auth.service';
import { ContextService } from '@app/shared/services/context.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { WindowToken } from '@app/shared/window.token';
import { PathwayFacade } from '@dg/pathways-rsm';
import { TranslateService } from '@ngx-translate/core';
import {
  PathwayFollower,
  PathwayFollowersService,
} from '@app/pathways/services/pathway-followers.service';

@Component({
  selector: 'dgx-pathway-followers',
  templateUrl: './pathway-followers.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PathwayFollowersComponent implements OnInit {
  public pathway: PathwayDetailsModel;

  @ViewChild('completion', { static: true })
  public completionTemplate: TemplateRef<any>;

  public readonly numResultsPerPage: number = 20;

  public followers: PathwayFollower[] = [];
  public isLoading: boolean = false;
  public moreItemsAvailable: boolean = false;
  public hasNoResults: boolean = false;
  public isDescending: boolean = true;
  public orderBy: string = 'name';

  public totalFollowers: number = 0;
  public followerCountFormat: string;
  public reportsRouterLink: any[];

  public i18n = this.translate.instant([
    'Core_Role',
    'Core_RoleAdmin',
    'Core_RoleCareerProfessional',
    'Core_RoleLearningProfessional',
    'Core_RoleManager',
    'Core_RoleMember',
    'Core_RoleTechnicalAdmin',
    'Pathways_CompletedColumn',
    'Pathways_FollowedDate',
    'dgEnrolled_NameColumn',
  ]);

  public columns: (DataColumn | DataColumnPerson | DataColumnDate)[] = [];

  private readonly ReportsRoute = '../insights/reporting';
  private skip = 0;

  constructor(
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private contextService: ContextService,
    private facade: PathwayFacade,
    private pathwayFollowersService: PathwayFollowersService,
    private translate: TranslateService,
    private trackerService: TrackerService,
    @Inject(WindowToken) private windowRef: Window
  ) {}

  public ngOnInit() {
    this.trackerService.setLocation('Pathway Followers');

    this.pathway = this.facade.snapshot.pathway;

    this.setColumns();
    this.setDownloadUrl();
    this.loadUsers();
    this.trackerService.trackEventData({
      action: 'Pathway Enrollees Viewed',
      properties: {
        internalUrl: this.pathway.publicUrl,
        id: this.pathway.id,
        title: this.pathway.title,
      },
    });
  }

  public getOrgRoleLabel(orgRole: PathwayFollower['orgRole']): string {
    switch (orgRole) {
      case 'Admin':
        return this.i18n.Core_RoleAdmin;
      case 'Technical Admin':
        return this.i18n.Core_RoleTechnicalAdmin;
      case 'Career Professional':
        return this.i18n.Core_RoleCareerProfessional;
      case 'Learning Professional':
        return this.i18n.Core_RoleLearningProfessional;
      case 'Manager':
        return this.i18n.Core_RoleManager;
      case 'Member':
        return this.i18n.Core_RoleMember;
      default:
        // anonymous user
        return '';
    }
  }

  public onSort(orderBy: string): void {
    if (this.isLoading) {
      return;
    }
    this.isDescending = !this.isDescending;
    this.orderBy = orderBy;
    this.skip = 0;
    this.loadUsers();
  }

  public onPageChange(page: number): void {
    this.skip = (page - 1) * this.numResultsPerPage;
    this.loadUsers();
  }

  private setDownloadUrl() {
    // Global admin org currently doesn't currently support permissions for canDownloadReports
    // So don't show download report when context=channel(GAT org) and canDownloadReports is true
    const isChannel = this.contextService.urlHasContext(
      this.windowRef.location.search,
      'channel'
    );
    const orgPermissions = this.authService.authUser.defaultOrgInfo.permissions;
    if (
      (isChannel && orgPermissions.downloadPathwayDetailsReport) ||
      (!isChannel &&
        (orgPermissions.downloadPathwayDetailsReport ||
          orgPermissions.downloadReports))
    ) {
      this.reportsRouterLink = [this.ReportsRoute];
    }
  }

  private loadUsers() {
    this.isLoading = true;
    this.pathwayFollowersService
      .getFollowers(
        this.pathway.id,
        this.skip,
        this.numResultsPerPage,
        this.orderBy,
        this.isDescending
      )
      .subscribe((pathwayEnrolledInsights) => {
        this.totalFollowers = pathwayEnrolledInsights.enrollmentCount;
        this.followerCountFormat = this.translate.instant(
          'Pathways_FollowingCountFormat',
          { count: pathwayEnrolledInsights.enrollmentCount }
        );
        const users = pathwayEnrolledInsights.enrolledUsers.map((user) => {
          // Format and translate the data so we can just show it in the data table
          return {
            ...user,
            completionTooltip: this.translate.instant(
              'dgEnrolled_CompletedTooltipFormat',
              {
                completedCount: user.completionCount,
                totalCount: user.totalPathwayItems,
              }
            ),
            enrollDateLabel: new Date(user.enrollDate).toLocaleDateString(),
            orgRoleLabel: this.getOrgRoleLabel(user.orgRole),
          };
        });
        this.moreItemsAvailable = pathwayEnrolledInsights.hasMoreItems;
        this.followers = users;
        this.hasNoResults = this.skip === 0 && users.length === 0;
        this.skip += this.numResultsPerPage;
        this.isLoading = false;
        this.cdr.detectChanges();
      });
  }

  private setColumns(): void {
    this.columns = [
      {
        type: 'person',
        label: this.i18n.dgEnrolled_NameColumn,
        picSize: 'tiny',
        prop: 'user',
        propPic: 'picture',
        propName: 'name',
        propHref: 'vanityUrl',
        canSort: true,
        sortName: 'name',
      } as DataColumnPerson,
      {
        label: this.i18n.Core_Role,
        prop: 'orgRole',
        canSort: true,
      } as DataColumn,
      {
        type: 'date',
        label: this.i18n.Pathways_FollowedDate,
        prop: 'enrollDate',
        canSort: true,
        dateFormat: 'M/dd/yyyy',
      } as DataColumnDate,
      {
        label: this.i18n.Pathways_CompletedColumn,
        template: this.completionTemplate,
        canSort: true,
        sortName: 'completedPercent',
        headClasses: 'l_flexbar l_flex-justify-right guts-p-r-1',
        bodyClasses: 'l_flexbar l_flex-justify-right guts-p-r-1',
      } as DataColumn,
    ];
  }
}
